// Transformation phase calculations based on Assimagra requirements
// These calculations represent the transformation of extracted materials

import { ProductEntry, CalculationInputs, MaterialUsage } from "@/types/product/productStep.types";
import { MaterialDocWithId } from "@/types/material";
import { TransportDistribution } from "@/types/transport-distribution/transport-distribution.types";
import { StoneType } from "@/types/stone-type/stone-type.types";
import { getFactorValue, convertToKgForCalculation } from "./conversions";
import { calculateProductSpecificMaterialFootprints } from "./extraction";
import { getFunctionalUnitMassInTonnes, convertToTonnes } from "./unitConversions";

/**
 * Determines the correct allocation ratio (physical or economic) for transformation phase carbon calculations.
 * @param generalData General data from the form.
 * @returns 'physical' or 'economic'.
 */
const getTransformationAllocationType = (generalData: CalculationInputs["generalData"]): "physical" | "economic" => {
  const pvpCaro = generalData.pvp_caro_trsf || 0;
  const pvpBarato = generalData.pvp_barato_trsf || 0;

  if (pvpBarato <= 0) {
    return "economic";
  }

  const rd = pvpCaro / pvpBarato;
  return rd <= 5 ? "physical" : "economic";
};

/**
 * Find the matching annual material entry from consumption data.
 * Replicates the matching logic from TransformationProductForm.
 */
const findAnnualMaterialEntry = (materialEntries: any[], material: MaterialUsage): any | undefined => {
  return materialEntries.find((entry: any) => {
    const byId = entry?.setup_item_id === material.materialId;
    const bySetupName = (entry?.setup_item_name || "").toLowerCase() === (material.materialName || "").toLowerCase();
    const byType = (entry?.materialType || "").toLowerCase() === (material.materialName || "").toLowerCase();
    const byName = (entry?.materialName || "").toLowerCase() === (material.materialName || "").toLowerCase();
    return byId || bySetupName || byType || byName;
  });
};

/**
 * Re-allocate material quantities at calculation time based on current allocation type.
 * This ensures materials are always allocated using the CURRENT general data (PVP values),
 * not stale quantities saved at form time.
 *
 * @param entry The product entry being calculated
 * @param allEntries All product entries (to find which products use each material)
 * @param calculationInputs Current calculation inputs with general data and consumptions
 * @returns Re-allocated materialsUsed (carbon) and materialsUsedHydric (hydric)
 */
const reallocateMaterials = (
  entry: ProductEntry,
  allEntries: ProductEntry[],
  calculationInputs: CalculationInputs
): { materialsUsed: MaterialUsage[]; materialsUsedHydric: MaterialUsage[] } => {
  const allocationType = getTransformationAllocationType(calculationInputs.generalData);
  const activityType = calculationInputs.generalData.activityType;

  // Get annual material entries from consumptions
  let materialEntries: any[] = [];
  if (activityType === "extraction_and_transformation") {
    materialEntries = calculationInputs.consumptions.transformation?.materials?.entries || [];
  } else {
    materialEntries =
      calculationInputs.consumptions.materials?.entries ||
      calculationInputs.consumptions.transformation?.materials?.entries ||
      [];
  }

  // If no annual entries available, fall back to saved quantities
  if (materialEntries.length === 0) {
    return {
      materialsUsed: entry.materialsUsed,
      materialsUsedHydric: entry.materialsUsedHydric || entry.materialsUsed,
    };
  }

  // All transformation entries for determining which products use each material
  // Ensure the current entry is included (it might not be in allEntries when adding a new product)
  const isEntryInList = allEntries.some((e) => e.id === entry.id);
  const allRelevantEntries = isEntryInList ? allEntries : [...allEntries, entry];
  const transformationEntries = allRelevantEntries.filter((e) => e.productFlow === "transformation");

  // Helper to allocate a single material for a given allocation mode
  const allocateMaterial = (material: MaterialUsage, mode: "physical" | "economic"): MaterialUsage => {
    const annualEntry = findAnnualMaterialEntry(materialEntries, material);
    if (!annualEntry || annualEntry.quantity === undefined) {
      return material; // Can't re-allocate without annual data
    }

    // Find all transformation products that use this material
    const productsUsingMaterial = transformationEntries.filter((e) =>
      e.materialsUsed.some(
        (m) => m.materialId === material.materialId || m.materialName === material.materialName
      )
    );

    if (productsUsingMaterial.length === 0) {
      return { ...material, quantity: 0, unit: annualEntry.unit || material.unit };
    }

    let totalForMaterial: number;
    let currentProductValue: number;

    if (mode === "physical") {
      totalForMaterial = productsUsingMaterial.reduce((sum, e) => sum + convertToTonnes(e).value, 0);
      currentProductValue = convertToTonnes(entry).value;
    } else {
      totalForMaterial = productsUsingMaterial.reduce((sum, e) => sum + (Number(e.productRevenue) || 0), 0);
      currentProductValue = Number(entry.productRevenue) || 0;
    }

    const materialSpecificRatio = totalForMaterial > 0 ? currentProductValue / totalForMaterial : 0;
    const allocatedQuantity = Number(annualEntry.quantity) * materialSpecificRatio;

    return { ...material, quantity: allocatedQuantity, unit: annualEntry.unit || material.unit };
  };

  // Carbon: uses rd-based allocation type
  const materialsUsed = entry.materialsUsed.map((mat) => allocateMaterial(mat, allocationType));

  // Hydric: ALWAYS uses economic allocation
  let materialsUsedHydric: MaterialUsage[];
  if (allocationType === "economic") {
    // Carbon already used economic, reuse same quantities
    materialsUsedHydric = materialsUsed;
  } else {
    // Carbon used physical, compute separate economic allocation for hydric
    materialsUsedHydric = entry.materialsUsed.map((mat) => allocateMaterial(mat, "economic"));
  }

  return { materialsUsed, materialsUsedHydric };
};

/**
 * Calculate hydric footprint for materials used by a transformation product
 * Uses the allocated material quantities and their hydric factors
 */
const calculateTransformationMaterialHydricFootprint = (
  materialsUsed: MaterialUsage[],
  materialDocs: MaterialDocWithId[]
): number => {
  let total = 0;

  for (const materialUsage of materialsUsed) {
    const materialDoc = materialDocs.find((doc) => doc.id === materialUsage.materialId);
    if (materialDoc && materialUsage.quantity > 0) {
      // Convert tons to kg if necessary
      const converted = convertToKgForCalculation(materialUsage.quantity, materialUsage.unit);
      const factor = getFactorValue(materialDoc, materialUsage.unit, "hydric");
      total += converted.quantity * factor;
    }
  }

  return total;
};

// --- Transformation Phase Calculations ---

const calculateTransformationCarbonMaterials = (
  entry: ProductEntry,
  _inputs: CalculationInputs,
  materialDocs: MaterialDocWithId[]
): number => {
  const { annualProduction } = entry;
  if (annualProduction <= 0) return 0;

  // Materials are already pre-allocated in the form (quantity = annualTotal * materialSpecificRatio)
  // No need to multiply by ratio again
  const totalCarbonMaterials = calculateProductSpecificMaterialFootprints(entry.materialsUsed, materialDocs, "carbon");

  const result = totalCarbonMaterials / annualProduction;

  return result;
};

const calculateTransformationCarbonEcr = (entry: ProductEntry, inputs: CalculationInputs): number => {
  const { annualProduction, ratioByQuantity, ratioByRevenue } = entry;
  if (annualProduction <= 0) return 0;

  let totalECR = 0;
  if (inputs.consumptions.transformation) {
    // Mixed company
    totalECR =
      (inputs.consumptions.transformation.electricity?.total_pc_ele || 0) +
      (inputs.consumptions.transformation.fuels?.total_pc_comb || 0) +
      (inputs.consumptions.transformation.residue?.total_pc_res || 0);
  } else {
    // Single activity company
    totalECR =
      (inputs.consumptions.electricity?.total_pc_ele || 0) +
      (inputs.consumptions.fuels?.total_pc_comb || 0) +
      (inputs.consumptions.residue?.total_pc_res || 0);
  }

  const allocationType = getTransformationAllocationType(inputs.generalData);
  const ratio = allocationType === "physical" ? ratioByQuantity : ratioByRevenue;

  const result = (ratio * totalECR) / annualProduction;

  // Don't round here to preserve precision for further calculations
  return result;
};

const calculateTransformationCarbonElectricityOnly = (entry: ProductEntry, inputs: CalculationInputs): number => {
  const { annualProduction, ratioByQuantity, ratioByRevenue } = entry;
  if (annualProduction <= 0) return 0;

  let totalElectricity = 0;
  if (inputs.consumptions.transformation) {
    // Mixed company
    totalElectricity = inputs.consumptions.transformation.electricity?.total_pc_ele || 0;
  } else {
    // Single activity company
    totalElectricity = inputs.consumptions.electricity?.total_pc_ele || 0;
  }
  const allocationType = getTransformationAllocationType(inputs.generalData);
  const ratio = allocationType === "physical" ? ratioByQuantity : ratioByRevenue;

  const result = (ratio * totalElectricity) / annualProduction;
  return result;
};

const calculateTransformationCarbonFuels = (entry: ProductEntry, inputs: CalculationInputs): number => {
  const { annualProduction, ratioByQuantity, ratioByRevenue } = entry;
  if (annualProduction <= 0) return 0;

  let totalFuels = 0;
  if (inputs.consumptions.transformation) {
    // Mixed company
    totalFuels = inputs.consumptions.transformation.fuels?.total_pc_comb || 0;
  } else {
    // Single activity company
    totalFuels = inputs.consumptions.fuels?.total_pc_comb || 0;
  }
  const allocationType = getTransformationAllocationType(inputs.generalData);
  const ratio = allocationType === "physical" ? ratioByQuantity : ratioByRevenue;

  const result = (ratio * totalFuels) / annualProduction;
  return result;
};

const calculateTransformationCarbonResidue = (entry: ProductEntry, inputs: CalculationInputs): number => {
  const { annualProduction, ratioByQuantity, ratioByRevenue } = entry;
  if (annualProduction <= 0) return 0;

  let totalResidue = 0;
  if (inputs.consumptions.transformation) {
    // Mixed company
    totalResidue = inputs.consumptions.transformation.residue?.total_pc_res || 0;
  } else {
    // Single activity company
    totalResidue = inputs.consumptions.residue?.total_pc_res || 0;
  }
  const allocationType = getTransformationAllocationType(inputs.generalData);
  const ratio = allocationType === "physical" ? ratioByQuantity : ratioByRevenue;

  const result = (ratio * totalResidue) / annualProduction;
  return result;
};

const calculateTransformationHydricMaterials = (
  entry: ProductEntry,
  inputs: CalculationInputs,
  materialDocs: MaterialDocWithId[]
): number => {
  const { functionalUnit, annualProduction, ratioByRevenue } = entry;
  const { generalData } = inputs;
  if (annualProduction <= 0) return 0;

  // Hydric materials use economic allocation - use materialsUsedHydric (always economic-allocated)
  const hydricMaterials = entry.materialsUsedHydric || entry.materialsUsed;
  const totalHydricMaterials = calculateProductSpecificMaterialFootprints(hydricMaterials, materialDocs, "hydric");

  // Get extraction materials per tonne (for m2 products that need upstream allocation)
  let extractionMaterialsTotal = 0;
  if (inputs.consumptions.extraction) {
    extractionMaterialsTotal = inputs.consumptions.extraction.materials?.total_ph_mat || 0;
  } else {
    extractionMaterialsTotal = inputs.consumptions.materials?.total_ph_mat || 0;
  }

  const totalExtractionProduction = generalData.prod_ext || 0;
  const extractionMaterialsPerTonne = totalExtractionProduction > 0 ? extractionMaterialsTotal / totalExtractionProduction : 0;

  // Materials are already pre-allocated with economic ratio in materialsUsedHydric
  // No need to multiply by ratio again
  const transformationMaterialsPerUnit = totalHydricMaterials / Number(annualProduction);

  let result = 0;
  if (functionalUnit.unit === "m2") {
    // For m² products, also add upstream extraction materials (per-tonne factor × mass)
    const thicknessCm = functionalUnit.thickness || entry.espessura || 0;
    const thicknessM = thicknessCm / 100;
    const density = generalData.stoneDensity || entry.density || 1;
    const extractionMaterialsPerUnit = functionalUnit.quantity * thicknessM * density * ratioByRevenue * extractionMaterialsPerTonne;
    result = transformationMaterialsPerUnit + extractionMaterialsPerUnit;
  } else {
    result = transformationMaterialsPerUnit;
  }

  return result;
};

const calculateTransformationHydricEcr = (entry: ProductEntry, inputs: CalculationInputs): number => {
  const { functionalUnit, annualProduction, ratioByRevenue } = entry;
  const { generalData } = inputs;
  if (annualProduction <= 0) return 0;

  let totalEC = 0;
  if (inputs.consumptions.transformation) {
    // Mixed company
    totalEC =
      (inputs.consumptions.transformation.electricity?.total_ph_ele || 0) + (inputs.consumptions.transformation.fuels?.total_ph_comb || 0);
  } else {
    // Single activity company
    totalEC =
      (inputs.consumptions.electricity?.total_ph_ele || 0) + (inputs.consumptions.fuels?.total_ph_comb || 0);
  }

  // For transformation products, we need to use the extraction energy per tonne
  // This is because transformation products use energy that was originally allocated to extraction
  let extractionEnergyPerTonne = 0;
  if (inputs.consumptions.extraction) {
    // Mixed company - use extraction energy
    extractionEnergyPerTonne =
      (inputs.consumptions.extraction.electricity?.total_ph_ele || 0) + (inputs.consumptions.extraction.fuels?.total_ph_comb || 0);
  } else {
    // Single activity company - use total energy
    extractionEnergyPerTonne =
      (inputs.consumptions.electricity?.total_ph_ele || 0) + (inputs.consumptions.fuels?.total_ph_comb || 0);
  }

  // Calculate extraction energy per tonne
  const totalExtractionProduction = inputs.generalData.prod_ext || 0;
  const extractionEnergyPerTonneValue = totalExtractionProduction > 0 ? extractionEnergyPerTonne / totalExtractionProduction : 0;

  // Hydric ALWAYS uses economic allocation (ratioByRevenue) - same as extraction
  // This is the product's actual revenue ratio and doesn't change with PVP test values

  let result = 0;
  if (functionalUnit.unit === "m2") {
    // For m² products, use per-tonne factor × mass of functional unit
    const thicknessCm = functionalUnit.thickness || entry.espessura || 0;
    const thicknessM = thicknessCm / 100;
    const density = generalData.stoneDensity || entry.density || 1;
    result = functionalUnit.quantity * thicknessM * density * ratioByRevenue * extractionEnergyPerTonneValue;
  } else {
    // For m3/kg/t products: annual total / count of functional units
    result = (ratioByRevenue * totalEC) / Number(annualProduction);
  }

  // Don't round here to preserve precision for further calculations
  return result;
};

const calculateTransformationHydricWater = (entry: ProductEntry, inputs: CalculationInputs): number => {
  const { functionalUnit, annualProduction, ratioByRevenue } = entry;
  const { generalData } = inputs;
  if (annualProduction <= 0) return 0;

  let totalWater = 0;
  let totalGrayWater = 0;
  if (inputs.consumptions.transformation) {
    // Mixed company
    totalWater = inputs.consumptions.transformation.water?.total_ph_agua || 0;
    totalGrayWater = inputs.consumptions.transformation.water?.total_ph_agua_cinzenta || 0;
  } else {
    // Single activity company
    totalWater = inputs.consumptions.water?.total_ph_agua || 0;
    totalGrayWater = inputs.consumptions.water?.total_ph_agua_cinzenta || 0;
  }
  
  // For transformation products, calculate extraction water per tonne (blue water)
  let extractionWaterTotal = 0;
  if (inputs.consumptions.extraction) {
    extractionWaterTotal = inputs.consumptions.extraction.water?.total_ph_agua || 0;
  } else {
    extractionWaterTotal = inputs.consumptions.water?.total_ph_agua || 0;
  }

  const totalExtractionProduction = inputs.generalData.prod_ext || 0;
  const extractionWaterPerTonne = totalExtractionProduction > 0 ? extractionWaterTotal / totalExtractionProduction : 0;

  // Calculate extraction gray water per tonne
  let extractionGrayWaterTotal = 0;
  if (inputs.consumptions.extraction) {
    extractionGrayWaterTotal = inputs.consumptions.extraction.water?.total_ph_agua_cinzenta || 0;
  } else {
    extractionGrayWaterTotal = inputs.consumptions.water?.total_ph_agua_cinzenta || 0;
  }
  const extractionGrayWaterPerTonne = totalExtractionProduction > 0 ? extractionGrayWaterTotal / totalExtractionProduction : 0;

  // Hydric ALWAYS uses economic allocation (ratioByRevenue) - same as extraction
  // This is the product's actual revenue ratio and doesn't change with PVP test values

  let resultBlue = 0;
  let resultGray = 0;

  // Blue water: annual total / count (Pattern A)
  resultBlue = (ratioByRevenue * totalWater) / Number(annualProduction);

  // Gray water: per-tonne factor × mass of functional unit (Pattern B)
  const massInTonnes = getFunctionalUnitMassInTonnes(entry);
  resultGray = massInTonnes * ratioByRevenue * extractionGrayWaterPerTonne;

  if (functionalUnit.unit === "m2") {
    // For m² products, blue water also uses per-tonne factor × mass
    const thicknessCm = functionalUnit.thickness || entry.espessura || 0;
    const thicknessM = thicknessCm / 100;
    const density = generalData.stoneDensity || entry.density || 1;
    resultBlue = functionalUnit.quantity * thicknessM * density * ratioByRevenue * extractionWaterPerTonne;
    resultGray = functionalUnit.quantity * thicknessM * density * ratioByRevenue * extractionGrayWaterPerTonne;
  }

  return resultBlue + resultGray;
};

// --- Upstream (Extraction & Transport) Calculations for Transformation Products ---

/**
 * Normalize a product's transport legs into a {vehicleType, distance}[] array.
 * Supports the multi-transport model (entry.transports) and falls back to the
 * legacy single-transport fields (transportType/transportDistance) so footprints
 * saved before multi-transport keep calculating correctly.
 */
function getTransportLegs(entry: ProductEntry): Array<{ vehicleType?: string; distance?: number }> {
  if (entry.transports && entry.transports.length > 0) {
    return entry.transports;
  }
  if (entry.transportType || entry.transportDistance) {
    return [{ vehicleType: entry.transportType, distance: entry.transportDistance }];
  }
  return [];
}

export function calculateTransportFootprint(
  entry: ProductEntry,
  transportDocs: TransportDistribution[],
  type: "carbon" | "hydric"
): number {
  const legs = getTransportLegs(entry);
  if (legs.length === 0) return 0;

  const docs = transportDocs || [];

  const rend = entry.rendimento || 100;

  // Mass in tonnes (per functional unit) — shared across all transport legs.
  let mass = 0;
  if (entry.functionalUnit.unit === "m2") {
    // Convert thickness from cm to meters, use functionalUnit.thickness if available, otherwise espessura
    const thicknessCm = entry.functionalUnit.thickness || entry.espessura || 0;
    const thicknessM = thicknessCm / 100;
    // Density is in t/m³, result is directly in tonnes
    mass = entry.functionalUnit.quantity * thicknessM * (entry.density || 0);
  } else if (entry.functionalUnit.unit === "m3") {
    // Density is in t/m³, result is directly in tonnes
    mass = entry.functionalUnit.quantity * (entry.density || 0);
  } else if (entry.functionalUnit.unit === "kg") {
    mass = entry.functionalUnit.quantity / 1000;
  }

  if (mass <= 0) return 0;

  // Sum the contribution of every transport leg (e.g. air + land).
  let total = 0;
  for (const leg of legs) {
    if (!leg.vehicleType || !leg.distance) continue;
    const transportDoc = docs.find((t) => t.vehicleType === leg.vehicleType);
    if (!transportDoc) continue;
    const factor =
      type === "carbon"
        ? transportDoc.carbon_factor?.value ?? transportDoc.fe_trans ?? 0
        : transportDoc.hydric_factor?.value ?? transportDoc.fh_trans ?? 0;
    // Result is already per functional unit (mass is per one UF)
    total += factor * leg.distance * mass * (100 / rend);
  }

  return total;
}

function calculateExtractionFootprintForTransformation(
  entry: ProductEntry,
  allEntries: ProductEntry[],
  stoneDocs: StoneType[]
): { pc_ext: number; ph_ext: number } {
  const rend = entry.rendimento || 100;
  const selectedStoneType = stoneDocs.find((s) => s.id === entry.stoneType);

  // Case 1: External, non-associated company (OT3.0 / OT6.0)
  if (entry.usesExternalStone === "yes" && entry.comesFromAssociated === "no") {
    const fe_ext_carbon = selectedStoneType?.average_ext_carbon_factor?.value || selectedStoneType?.fe_extracao?.value || 0;
    const fe_ext_hydric = selectedStoneType?.average_ext_hydric_factor?.value || selectedStoneType?.fh_extracao?.value || 0;

    let massInKg = 0;
    if (entry.functionalUnit.unit === "m2") {
      const thicknessCm = entry.functionalUnit.thickness || entry.espessura || 0;
      const thicknessM = thicknessCm / 100;
      massInKg = entry.functionalUnit.quantity * thicknessM * (entry.density || 0) * 1000;
    } else if (entry.functionalUnit.unit === "m3") {
      massInKg = entry.functionalUnit.quantity * (entry.density || 0) * 1000;
    } else if (entry.functionalUnit.unit === "kg") {
      massInKg = entry.functionalUnit.quantity;
    }

    // Convert kg to tonnes for factor application
    const massInTonnes = massInKg / 1000;

    const pc_ext = fe_ext_carbon * massInTonnes * (100 / rend);
    const ph_ext = fe_ext_hydric * massInTonnes * (100 / rend);

    return { pc_ext, ph_ext };
  }

  // Case 2: Own extraction or Associated Company (OT3.1 / OT7.0)
  const originProductId = entry.originProductId;
  if (!originProductId) {
    // Fallback to Case 1: Use stone type factors when no origin product is specified
    const fe_ext_carbon = selectedStoneType?.average_ext_carbon_factor?.value || selectedStoneType?.fe_extracao?.value || 0;
    const fe_ext_hydric = selectedStoneType?.average_ext_hydric_factor?.value || selectedStoneType?.fh_extracao?.value || 0;

    let massInKg = 0;
    if (entry.functionalUnit.unit === "m2") {
      const thicknessCm = entry.functionalUnit.thickness || entry.espessura || 0;
      const thicknessM = thicknessCm / 100;
      massInKg = entry.functionalUnit.quantity * thicknessM * (entry.density || 0) * 1000;
    } else if (entry.functionalUnit.unit === "m3") {
      massInKg = entry.functionalUnit.quantity * (entry.density || 0) * 1000;
    } else if (entry.functionalUnit.unit === "kg") {
      massInKg = entry.functionalUnit.quantity;
    }

    // Convert kg to tonnes for factor application
    const massInTonnes = massInKg / 1000;

    const pc_ext = fe_ext_carbon * massInTonnes * (100 / rend);
    const ph_ext = fe_ext_hydric * massInTonnes * (100 / rend);

    return { pc_ext, ph_ext };
  }

  const originProduct = allEntries.find((e) => e.id === originProductId);
  if (!originProduct) {
    return { pc_ext: 0, ph_ext: 0 };
  }

  // These are the total footprints per functional unit of the origin product
  const pc_origin_total = originProduct.carbonFootprintTotal || 0;
  const ph_origin_total = originProduct.hydricFootprintTotal || 0;

  let originMassInTonnes = 0;
  if (originProduct.functionalUnit.unit === "t") {
    originMassInTonnes = originProduct.functionalUnit.quantity;
  } else if (originProduct.functionalUnit.unit === "m3") {
    originMassInTonnes = originProduct.functionalUnit.quantity * (originProduct.density || 0);
  }

  if (originMassInTonnes <= 0) {
    return { pc_ext: 0, ph_ext: 0 };
  }

  const fe_ext_carbon_total = pc_origin_total / originMassInTonnes;
  const fe_ext_hydric_total = ph_origin_total / originMassInTonnes;

  let targetMassInTonnes = 0;
  if (entry.functionalUnit.unit === "m2") {
    const thicknessCm = entry.functionalUnit.thickness || entry.espessura || 0;
    const thicknessM = thicknessCm / 100;
    targetMassInTonnes = entry.functionalUnit.quantity * thicknessM * (entry.density || 0);
  } else if (entry.functionalUnit.unit === "m3") {
    targetMassInTonnes = entry.functionalUnit.quantity * (entry.density || 0);
  } else if (entry.functionalUnit.unit === "kg") {
    targetMassInTonnes = entry.functionalUnit.quantity / 1000;
  }

  const pc_ext = fe_ext_carbon_total * targetMassInTonnes * (100 / rend);
  const ph_ext = fe_ext_hydric_total * targetMassInTonnes * (100 / rend);

  return { pc_ext, ph_ext };
}

// --- Main Calculation Function ---

export function calculateTransformationProductFootprints(
  entry: ProductEntry,
  allEntries: ProductEntry[],
  calculationInputs: CalculationInputs,
  stoneDocs: StoneType[],
  transportDocs: TransportDistribution[],
  materialDocs: MaterialDocWithId[]
) {
  // Re-allocate materials at calculation time using current general data (PVP values)
  // This ensures allocation type (physical vs economic) is always up-to-date
  const reallocated = reallocateMaterials(entry, allEntries, calculationInputs);
  entry.materialsUsed = reallocated.materialsUsed;
  entry.materialsUsedHydric = reallocated.materialsUsedHydric;

  // Calculate and add footprint values directly to entry.materialsUsed
  if (entry.materialsUsed && entry.materialsUsed.length > 0) {
    entry.materialsUsed = entry.materialsUsed.map((mat) => {
      const matDoc = materialDocs?.find((m) => m.id === mat.materialId);

      if (!matDoc) {
        console.warn(`[transformation.ts] Material document not found for materialId: ${mat.materialId}, materialName: ${mat.materialName}`);
      }

      const carbon = matDoc ? getFactorValue(matDoc, mat.unit, "carbon") * mat.quantity : 0;
      const hydric = matDoc ? getFactorValue(matDoc, mat.unit, "hydric") * mat.quantity : 0;

      return {
        ...mat,
        carbon_footprint: carbon,
        hydric_footprint: hydric,
      };
    });
  }

  // 1. Calculate Upstream Footprints (Extraction & Transport)
  let { pc_ext, ph_ext } = calculateExtractionFootprintForTransformation(entry, allEntries, stoneDocs);
  
  // Calculate extraction water portion for energy footprint (electricity + fuels)
  let ph_ext_energy = 0;
  if (ph_ext === 0) {
    let totalTransformationEnergy = 0;
    if (calculationInputs.consumptions.transformation) {
      totalTransformationEnergy =
        (calculationInputs.consumptions.transformation.electricity?.total_ph_ele || 0) +
        (calculationInputs.consumptions.transformation.fuels?.total_ph_comb || 0);
    } else {
      totalTransformationEnergy =
        (calculationInputs.consumptions.electricity?.total_ph_ele || 0) +
        (calculationInputs.consumptions.fuels?.total_ph_comb || 0);
    }

    const energyPerUnit = entry.annualProduction > 0 ? totalTransformationEnergy / entry.annualProduction : 0;

    // Hydric ALWAYS uses economic allocation (ratioByRevenue), not rd-based ratio
    ph_ext_energy = energyPerUnit * entry.ratioByRevenue;
  }
  
  // Calculate extraction water portion for water footprint
  let ph_ext_water = 0;
  let totalTransformationWater = 0;
  if (calculationInputs.consumptions.transformation) {
    totalTransformationWater = calculationInputs.consumptions.transformation.water?.total_ph_agua || 0;
  } else {
    totalTransformationWater = calculationInputs.consumptions.water?.total_ph_agua || 0;
  }

  const waterPerUnit = entry.annualProduction > 0 ? totalTransformationWater / entry.annualProduction : 0;

  // Hydric ALWAYS uses economic allocation (ratioByRevenue), not rd-based ratio
  ph_ext_water = waterPerUnit * entry.ratioByRevenue;
  
  // Calculate extraction materials portion for materials footprint
  let ph_ext_materials = 0;
  const totalHydricMaterials = calculateTransformationMaterialHydricFootprint(entry.materialsUsed, materialDocs);
  const materialsPerUnit = entry.annualProduction > 0 ? totalHydricMaterials / entry.annualProduction : 0;

  // Hydric ALWAYS uses economic allocation (ratioByRevenue), not rd-based ratio
  ph_ext_materials = materialsPerUnit * entry.ratioByRevenue;
  const pc_trans = calculateTransportFootprint(entry, transportDocs, "carbon");
  const ph_trans = calculateTransportFootprint(entry, transportDocs, "hydric");

  // 2. Calculate Transformation Phase Footprints
  const pc_trsf_materiais = calculateTransformationCarbonMaterials(entry, calculationInputs, materialDocs);
  const pc_trsf_ecr = calculateTransformationCarbonEcr(entry, calculationInputs);

  // Individual component calculations for transformation
  const pc_ele = calculateTransformationCarbonElectricityOnly(entry, calculationInputs);
  const pc_comb = calculateTransformationCarbonFuels(entry, calculationInputs);
  const pc_res = calculateTransformationCarbonResidue(entry, calculationInputs);

  const ph_trsf_materiais = calculateTransformationHydricMaterials(entry, calculationInputs, materialDocs);
  const ph_trsf_ec = calculateTransformationHydricEcr(entry, calculationInputs);
  const ph_trsf_agua = calculateTransformationHydricWater(entry, calculationInputs);

  // 3. Aggregate Results
  const pc_trsf = pc_trsf_materiais + pc_trsf_ecr;
  const ph_trsf = ph_trsf_materiais + ph_trsf_ec + ph_trsf_agua;

  const pc_prod = pc_ext + pc_trans + pc_trsf;
  const ph_prod = ph_ext + ph_trans + ph_trsf;

  return {
    // Phase-specific results for detailed view
    pc_ext_total: pc_ext,
    pc_trsf_total: pc_trsf,
    pc_trans_total: pc_trans,
    ph_ext_total: ph_ext,
    ph_trsf_total: ph_trsf,
    ph_trans_total: ph_trans,

    // Detailed breakdown for reporting
    pc_trsf_materiais,
    pc_trsf_ecr,

    // Individual components for disaggregated view
    pc_ele,
    pc_comb,
    pc_res,

    ph_trsf_materiais,
    ph_trsf_ec,
    ph_trsf_agua,

    // Final product footprints
    carbonFootprintTotal: pc_prod,
    hydricFootprintTotal: ph_prod,
  };
}
